import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';

import { AccaBoostConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Decimal } from 'decimal.js';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';

import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../../../base/services/betslip-messages-provider.service';
import { ITaxationInfoViewModel } from '../../../../taxation/components/digital/taxation-info.component';
import { RewardTypeData, TokenPayoutSummary } from '../../../models/betslip-result-summary.model';
import { ISucceededSlipRewardTokenInfo } from '../../../models/result-succeeded-slip.model';
import { IRewardTokenBadge } from '../../../models/reward-token-badge.model';
import { RewardTokenBadgeService } from '../../../services/reward-token-badge.service';

@Component({
    selector: 'bs-digital-linear-result-bet-returns',
    templateUrl: './linear-result-bet-returns.component.html',
    styleUrls: ['./linear-result-bet-returns.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LinearResultBetReturnsComponent implements OnInit {
    @Input() stake?: number;
    @Input() stakeMultiplier?: string;

    @Input() set possibleWinningsNet(value: Decimal | undefined) {
        this._possibleWinningsNet = value ?? new Decimal(0);
    }

    @Input() set possibleWinningsGross(value: Decimal | undefined) {
        this._possibleWinningsGross = value ?? new Decimal(0);
    }

    @Input() rewardTokenInfo?: ISucceededSlipRewardTokenInfo;
    @Input() boostedValue?: Decimal;

    _possibleWinningsNet = new Decimal(0);
    _possibleWinningsGross = new Decimal(0);

    readonly stakeText: string;
    readonly totalReturnsText: string;
    readonly totalReturnsNetText: string;
    readonly RewardTokenType = RewardTokenType;
    badge: IRewardTokenBadge | undefined;
    tokenPayout: TokenPayoutSummary | undefined;
    isFreeBetSelected: boolean;
    isTaxationInfoVisible: boolean;

    constructor(
        public siteCore: Sitecore,
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: ILinearBetslipMessagesProviderService,
        private rewardTokenBadgeService: RewardTokenBadgeService,
        private accaBoostConfig: AccaBoostConfig,
    ) {
        const messages = messagesProvider.getMessages();
        this.stakeText = messages.linear.StakeForThisBet;
        this.totalReturnsText = messages.linear.PotentialPayout;
        this.totalReturnsNetText = messages.linear.TotalReturnsNet;
    }

    ngOnInit(): void {
        if (!this.rewardTokenInfo) {
            return;
        }

        const tokenData: RewardTypeData = {
            tokenType: this.rewardTokenInfo.usedRewardTokenType,
            rewardTargetType: this.rewardTokenInfo.usedRewardTokenTargetType,
            boostedByRatio: this.rewardTokenInfo.boostedByRatio,
        };

        this.badge = this.rewardTokenBadgeService.getBadge(tokenData, this.stake);
        this.isFreeBetSelected = this.rewardTokenInfo.usedRewardTokenType === RewardTokenType.FreeBet;

        if (!this.rewardTokenInfo.rewardPayoutSummary) {
            return;
        }
        this.tokenPayout = this.isEnhancedAccaBoostLayoutEnabled
            ? undefined
            : {
                  summary: this.rewardTokenInfo.rewardPayoutSummary,
                  tokenType: this.rewardTokenInfo.usedRewardTokenType,
              };
    }

    hideTaxation(): boolean {
        return this._possibleWinningsGross.equals(0) || this._possibleWinningsGross.equals(this._possibleWinningsNet);
    }

    displayTaxationInfo(): void {
        this.isTaxationInfoVisible = true;
    }

    getTaxationVm(): ITaxationInfoViewModel {
        return {
            possibleWinningsGross: this._possibleWinningsGross,
            possibleWinningsNet: this._possibleWinningsNet,
            taxationAmount: this._possibleWinningsGross.minus(this._possibleWinningsNet),
            taxationRate: null,
        };
    }

    private get isEnhancedAccaBoostLayoutEnabled(): boolean {
        return this.accaBoostConfig.isEnhancedLayoutEnabled && this.badge?.typeData?.tokenType === RewardTokenType.AccaBoost;
    }
}
